import React, { useState } from "react";

import DiceFace from "../components/DiceFace.jsx";
import {
  ABILITY_KEYS,
  ABILITY_NAMES,
  STANDARD_ARRAY,
  POINT_BUY_COSTS,
  POINT_BUY_BUDGET,
  POINT_BUY_MIN,
  POINT_BUY_MAX,
  getTotalBonus,
  getAttributeTotal,
  getHpFixedPerLevel,
  calculateHP,
  mod,
  modStr,
  buildRolledSets,
  calcPointBuyCost,
  pointBuyRemaining,
} from "../characterCreatorUtils.js";

const METHOD_META = {
  roll: { icon: "🎲", label: "Rolagem de Dados", desc: "4d6, descarta o menor" },
  standard: { icon: "📋", label: "Array Padrão", desc: "15 · 14 · 13 · 12 · 10 · 8" },
  pointbuy: { icon: "🧮", label: "Point Buy", desc: "27 pontos para distribuir" },
};

// ── METHOD SELECTOR ─────────────────────────────────────────

function MethodSelector({ attrMode, activePanel, onSelectPanel }) {
  return (
    <div className="method-selector">
      {Object.entries(METHOD_META).map(([id, meta]) => {
        const isActive = activePanel === id;
        const isLocked = attrMode !== null && attrMode !== id;
        const lockedLabel = attrMode ? METHOD_META[attrMode]?.label : "";
        return (
          <button
            key={id}
            type="button"
            className={`method-card${isActive ? " method-card--active" : ""}${isLocked ? " method-card--disabled" : ""}`}
            onClick={() => !isLocked && onSelectPanel(id)}
            disabled={isLocked}
            title={isLocked ? `Você já iniciou "${lockedLabel}". Reinicie a criação para alterar.` : ""}
          >
            <span className="method-card__icon">{meta.icon}</span>
            <span className="method-card__label">{meta.label}</span>
            <span className="method-card__desc">{meta.desc}</span>
            {isLocked && <span className="method-card__lock">🔒</span>}
          </button>
        );
      })}
    </div>
  );
}

// ── ROLL METHOD ──────────────────────────────────────────────

function RollPanel({ state, onRollSets, onAssignRollSlot, onConfirmAttributes }) {
  const [isRolling, setIsRolling] = useState(false);
  const { rolledSets, rollAssignments } = state;
  const hasRolled = Array.isArray(rolledSets) && rolledSets.length === 6;

  function handleRoll() {
    if (isRolling) return;
    setIsRolling(true);
    setTimeout(() => {
      onRollSets(buildRolledSets());
      setIsRolling(false);
    }, 560);
  }

  const allAssigned =
    hasRolled &&
    ABILITY_KEYS.every((k) => rollAssignments?.[k] !== null && rollAssignments?.[k] !== undefined);

  function buildFinalAttributes() {
    return Object.fromEntries(ABILITY_KEYS.map((k) => [k, rolledSets[rollAssignments[k]].total]));
  }

  return (
    <div className="attr-method-panel">
      <button type="button" className="roll-btn" onClick={handleRoll} disabled={isRolling}>
        {isRolling ? "⏳ Rolando…" : hasRolled ? "🎲 Rolar Novamente" : "🎲 Rolar Atributos"}
      </button>

      {hasRolled && (
        <>
          <div className={`rolled-sets-grid${isRolling ? " dice-rolling" : ""}`}>
            {rolledSets.map((set, i) => {
              const assignedAbility = ABILITY_KEYS.find((k) => rollAssignments?.[k] === i);
              return (
                <div key={i} className={`rolled-set${assignedAbility ? " rolled-set--assigned" : ""}`}>
                  <div className="rolled-dice">
                    {set.dice.map((d, j) => (
                      <DiceFace key={j} value={d} sides={6} dropped={j === set.dropped} rolling={isRolling} />
                    ))}
                  </div>
                  <div className="rolled-formula">
                    {set.dice
                      .map((d, j) =>
                        j === set.dropped ? (
                          <s key={j} className="die-val-dropped">
                            {d}
                          </s>
                        ) : (
                          <span key={j} className="die-val">
                            {d}
                          </span>
                        )
                      )
                      .reduce(
                        (acc, el, j) =>
                          j === 0 ? [el] : [...acc, <span key={`s${j}`} className="die-sep"> + </span>, el],
                        []
                      )}
                    <span className="rolled-total">
                      {" "}= <strong>{set.total}</strong>
                    </span>
                  </div>
                  {assignedAbility && (
                    <div className="assigned-badge">{ABILITY_NAMES[assignedAbility].slice(0, 3).toUpperCase()}</div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="assign-grid">
            {ABILITY_KEYS.map((key) => {
              const bonus = getTotalBonus(state, key);
              const idx = rollAssignments?.[key] ?? null;
              const base = idx !== null ? rolledSets[idx].total : null;
              const total = base !== null ? base + bonus : null;
              return (
                <div key={key} className="assign-row-new">
                  <span className="assign-ability">{ABILITY_NAMES[key]}</span>
                  <div className="assign-pills">
                    {rolledSets.map((set, i) => {
                      const isActive = idx === i;
                      const ownerKey = ABILITY_KEYS.find((k) => k !== key && rollAssignments?.[k] === i);
                      return (
                        <button
                          key={i}
                          type="button"
                          className={`assign-pill${isActive ? " assign-pill--active" : ""}${ownerKey ? " assign-pill--used" : ""}`}
                          onClick={() => onAssignRollSlot(key, i)}
                          title={ownerKey ? `Já atribuído a ${ABILITY_NAMES[ownerKey]}` : `Atribuir ${set.total} a ${ABILITY_NAMES[key]}`}
                        >
                          <span className="assign-pill-val">{set.total}</span>
                          {ownerKey && <span className="assign-pill-owner">{ownerKey.toUpperCase()}</span>}
                        </button>
                      );
                    })}
                  </div>
                  <span className="assign-final">
                    {total !== null ? (
                      <><strong>{total}</strong> ({modStr(total)}){bonus > 0 && <em className="origin-bonus"> +{bonus}</em>}</>
                    ) : "—"}
                  </span>
                </div>
              );
            })}
          </div>

          {allAssigned && (
            <button
              type="button"
              className="confirm-btn"
              onClick={() => onConfirmAttributes(buildFinalAttributes(), "roll")}
            >
              ✓ Confirmar Atributos
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ── STANDARD ARRAY ───────────────────────────────────────────

function StandardPanel({ state, onAssignStandard, onConfirmAttributes }) {
  const { standardAssignments } = state;
  const usedValues = ABILITY_KEYS.map((k) => standardAssignments?.[k]).filter(
    (v) => v !== null && v !== undefined
  );
  const allAssigned = ABILITY_KEYS.every((k) => {
    const v = standardAssignments?.[k];
    return v !== null && v !== undefined;
  });

  function buildFinalAttributes() {
    return Object.fromEntries(ABILITY_KEYS.map((k) => [k, standardAssignments[k]]));
  }

  return (
    <div className="attr-method-panel">
      <div className="assign-grid">
        {ABILITY_KEYS.map((key) => {
          const bonus = getTotalBonus(state, key);
          const base = standardAssignments?.[key] ?? null;
          const total = base !== null ? base + bonus : null;
          return (
            <div key={key} className="assign-row-new">
              <span className="assign-ability">{ABILITY_NAMES[key]}</span>
              <div className="assign-pills">
                {STANDARD_ARRAY.map((v) => {
                  const isActive = base === v;
                  const ownerKey = ABILITY_KEYS.find((k) => k !== key && standardAssignments?.[k] === v);
                  return (
                    <button
                      key={v}
                      type="button"
                      className={`assign-pill${isActive ? " assign-pill--active" : ""}${ownerKey ? " assign-pill--used" : ""}`}
                      onClick={() => onAssignStandard(key, v)}
                      title={ownerKey ? `Já atribuído a ${ABILITY_NAMES[ownerKey]}` : undefined}
                    >
                      <span className="assign-pill-val">{v}</span>
                      {ownerKey && <span className="assign-pill-owner">{ownerKey.toUpperCase()}</span>}
                    </button>
                  );
                })}
              </div>
              <span className="assign-final">
                {total !== null ? (
                  <><strong>{total}</strong> ({modStr(total)}){bonus > 0 && <em className="origin-bonus"> +{bonus}</em>}</>
                ) : "—"}
              </span>
            </div>
          );
        })}
      </div>

      {allAssigned && (
        <button
          type="button"
          className="confirm-btn"
          onClick={() => onConfirmAttributes(buildFinalAttributes(), "standard")}
        >
          ✓ Confirmar Atributos
        </button>
      )}
    </div>
  );
}

// ── POINT BUY ────────────────────────────────────────────────

function PointBuyPanel({ state, onPointBuyChange, onConfirmAttributes }) {
  const { pointBuyValues } = state;
  const remaining = pointBuyRemaining(pointBuyValues);

  function buildFinalAttributes() {
    return { ...pointBuyValues };
  }

  return (
    <div className="attr-method-panel">
      <div
        className={`pb-budget${remaining === 0 ? " pb-budget--done" : remaining < 0 ? " pb-budget--over" : ""}`}
      >
        <span className="pb-budget-label">Pontos restantes:</span>
        <span className="pb-budget-value">
          <strong>{remaining}</strong>
          <span className="pb-budget-total"> / {POINT_BUY_BUDGET}</span>
        </span>
      </div>

      <div className="pb-grid">
        {ABILITY_KEYS.map((key) => {
          const bonus = getTotalBonus(state, key);
          const val = pointBuyValues?.[key] ?? POINT_BUY_MIN;
          const total = val + bonus;
          const cost = POINT_BUY_COSTS[val] ?? 0;
          const costNext = POINT_BUY_COSTS[val + 1];
          const canIncrease =
            val < POINT_BUY_MAX && costNext !== undefined && remaining >= costNext - cost;
          const canDecrease = val > POINT_BUY_MIN;

          return (
            <div key={key} className="pb-row">
              <span className="pb-ability">{ABILITY_NAMES[key]}</span>
              <div className="pb-controls">
                <button
                  type="button"
                  className="pb-btn"
                  onClick={() => onPointBuyChange(key, -1)}
                  disabled={!canDecrease}
                >
                  −
                </button>
                <span className="pb-value">{val}</span>
                <button
                  type="button"
                  className="pb-btn"
                  onClick={() => onPointBuyChange(key, +1)}
                  disabled={!canIncrease}
                >
                  +
                </button>
              </div>
              <span className="pb-total">
                {total} ({modStr(total)})
                {bonus > 0 && <em className="origin-bonus"> +{bonus}</em>}
              </span>
              <span className="pb-cost">Custo: {cost}</span>
            </div>
          );
        })}
      </div>

      {remaining >= 0 && (
        <button
          type="button"
          className="confirm-btn"
          onClick={() => onConfirmAttributes(buildFinalAttributes(), "pointbuy")}
        >
          ✓ Confirmar Atributos
        </button>
      )}
    </div>
  );
}

// ── CONFIRMED ATTR SUMMARY ───────────────────────────────────

function ConfirmedAttrSummary({ state }) {
  return (
    <div className="confirmed-summary">
      <div className="confirmed-badge">🔒 Atributos Confirmados</div>
      <div className="confirmed-attrs-grid">
        {ABILITY_KEYS.map((key) => {
          const total = getAttributeTotal(state, key);
          const bonus = getTotalBonus(state, key);
          return (
            <div key={key} className="confirmed-attr-cell">
              <div className="confirmed-attr-label">{key.toUpperCase()}</div>
              <div className="confirmed-attr-val">{total}</div>
              <div className="confirmed-attr-mod">{modStr(total)}</div>
              {bonus > 0 && <div className="confirmed-attr-bonus">+{bonus}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── HP SECTION ───────────────────────────────────────────────

function HPSection({ state, onSelectHpMethod, onRollHp, onConfirmHp, onConfirmHpFixed }) {
  const [isRolling, setIsRolling] = useState(false);

  const hitDie = state.charClass?.hit_die || 8;
  const level = state.startingPointData?.level || 1;
  const diceNeeded = level - 1;
  const fixedPerLevel = getHpFixedPerLevel(hitDie);
  const conTotal = getAttributeTotal(state, "con");
  const conMod = mod(conTotal);
  const { hpMethodLocked, hpRolledValues } = state;
  const conModLabel = conMod >= 0 ? `+${conMod}` : `${conMod}`;

  // HP calculations
  const hpConBonus = conMod * level;
  const hpFixed = hitDie + fixedPerLevel * diceNeeded + hpConBonus;
  const rolledSum =
    Array.isArray(hpRolledValues) && hpRolledValues.length === diceNeeded
      ? hpRolledValues.reduce((a, b) => a + b, 0)
      : null;
  const hpRolled = rolledSum !== null ? hitDie + rolledSum + hpConBonus : null;

  function handleRollDice() {
    if (isRolling) return;
    setIsRolling(true);
    setTimeout(() => {
      const values = Array.from({ length: diceNeeded }, () => Math.floor(Math.random() * hitDie) + 1);
      onRollHp(values);
      setIsRolling(false);
    }, 560);
  }

  // ─── FÓRMULA DE BREAKDOWN ───────────────────────────────────

  function FormulaBreakdown({ showRollSlots }) {
    const levelRange = level === 3 ? "2–3" : `2–${level}`;
    return (
      <div className="hp-breakdown">
        <div className="hp-breakdown-row">
          <span className="hp-breakdown-label">[Nível 1]</span>
          <span className="hp-breakdown-value">
            {hitDie} <em>(máximo do d{hitDie})</em>
          </span>
        </div>
        {diceNeeded > 0 && (
          <div className="hp-breakdown-row">
            <span className="hp-breakdown-label">[Níveis {levelRange}]</span>
            <span className="hp-breakdown-value">
              {showRollSlots
                ? `+ ${diceNeeded}× d${hitDie} (a rolar)`
                : `+ ${diceNeeded}× ${fixedPerLevel} = ${fixedPerLevel * diceNeeded}`}
              {" "}
              <em>(valor fixo: {fixedPerLevel} por nível)</em>
            </span>
          </div>
        )}
        <div className="hp-breakdown-row">
          <span className="hp-breakdown-label">[Bônus CON]</span>
          <span className="hp-breakdown-value">
            {conModLabel} × {level} {level === 1 ? "nível" : "níveis"} ={" "}
            {hpConBonus >= 0 ? `+${hpConBonus}` : hpConBonus}
          </span>
        </div>
        <div className="hp-breakdown-divider" />
        {showRollSlots ? (
          <div className="hp-breakdown-total">
            Total se rolar:{" "}
            <strong>
              {hitDie} + [?{diceNeeded > 1 ? ` × ${diceNeeded}` : ""}] + {hpConBonus >= 0 ? `+${hpConBonus}` : hpConBonus}
            </strong>
          </div>
        ) : (
          <div className="hp-breakdown-total">
            Total fixo: <strong>{hpFixed} HP</strong>
          </div>
        )}
      </div>
    );
  }

  // ─── NÍVEL 1: opção única (sem rolagem) ────────────────────

  if (level === 1) {
    return (
      <div className="hp-section-inner">
        <div className="hp-formula-info">
          <div className="hp-info-row">
            <span className="hp-info-icon">⚔️</span>
            <span>
              Dado de Vida: <strong>d{hitDie}</strong>
              {state.charClass && <span className="hp-info-class"> ({state.charClass.name})</span>}
            </span>
          </div>
          <div className="hp-info-row">
            <span className="hp-info-icon">💪</span>
            <span>
              Constituição: <strong>{conTotal}</strong> → Modificador <strong>{conModLabel}</strong>
            </span>
          </div>
        </div>
        <FormulaBreakdown showRollSlots={false} />
        <button type="button" className="confirm-btn" onClick={onConfirmHpFixed}>
          ✓ Confirmar {hpFixed} HP
        </button>
      </div>
    );
  }

  // ─── NÍVEIS 3 / 5: escolha de método ───────────────────────

  // Aguardando escolha
  if (!hpMethodLocked) {
    return (
      <div className="hp-section-inner">
        <div className="hp-formula-info">
          <div className="hp-info-row">
            <span className="hp-info-icon">⚔️</span>
            <span>
              Dado de Vida: <strong>d{hitDie}</strong>
              {state.charClass && <span className="hp-info-class"> ({state.charClass.name})</span>}
            </span>
          </div>
          <div className="hp-info-row">
            <span className="hp-info-icon">💪</span>
            <span>
              Constituição: <strong>{conTotal}</strong> → Modificador <strong>{conModLabel}</strong>
            </span>
          </div>
          <div className="hp-info-row hp-info-row--hint">
            <span className="hp-info-icon">ℹ️</span>
            <span>
              Você é nível {level}. O HP base (nível 1) é <strong>{hitDie}</strong>. Para os{" "}
              {diceNeeded} níveis seguintes, escolha entre valor fixo ou dado.
            </span>
          </div>
        </div>

        <div className="hp-method-row">
          <button
            type="button"
            className="hp-method-btn"
            onClick={() => onSelectHpMethod("max")}
            disabled={isRolling}
          >
            <span className="hp-method-icon">📋</span>
            <strong className="hp-method-name">Valor Fixo</strong>
            <span className="hp-method-desc">
              {fixedPerLevel} por nível · Total garantido
            </span>
            <span className="hp-method-formula">
              {hitDie} + {fixedPerLevel * diceNeeded} + {hpConBonus >= 0 ? `+${hpConBonus}` : hpConBonus} ={" "}
              <strong>{hpFixed} HP</strong>
            </span>
          </button>

          <button
            type="button"
            className="hp-method-btn"
            onClick={handleRollDice}
            disabled={isRolling}
          >
            <span className="hp-method-icon">🎲</span>
            <strong className="hp-method-name">Rolar {diceNeeded}× d{hitDie}</strong>
            <span className="hp-method-desc">Sorte do destino</span>
            {isRolling ? (
              <span className="hp-rolling-text">⏳ Rolando…</span>
            ) : (
              <span className="hp-method-click">Clique para rolar</span>
            )}
          </button>
        </div>
      </div>
    );
  }

  // ─── Método FIXO selecionado ────────────────────────────────

  if (hpMethodLocked === "max") {
    return (
      <div className="hp-section-inner">
        <div className="hp-method-chosen-badge">📋 Valor Fixo selecionado</div>
        <FormulaBreakdown showRollSlots={false} />
        <button type="button" className="confirm-btn" onClick={onConfirmHp}>
          ✓ Confirmar {hpFixed} HP
        </button>
      </div>
    );
  }

  // ─── Método ROLAR selecionado ───────────────────────────────

  return (
    <div className="hp-section-inner">
      <div className="hp-method-chosen-badge">🎲 Rolagem de Dados selecionada</div>

      {isRolling && <div className="hp-rolling-text hp-rolling-center">⏳ Rolando dados…</div>}

      {!isRolling && hpRolledValues && hpRolledValues.length === diceNeeded && (
        <>
          <div className="hp-dice-row">
            {hpRolledValues.map((v, i) => (
              <div key={i} className="hp-die-slot">
                <DiceFace value={v} sides={hitDie} size={56} />
                <span className="hp-die-label">Nv {i + 2}</span>
              </div>
            ))}
          </div>

          <div className="hp-roll-formula">
            <span className="hp-roll-formula-text">
              {hitDie}{" "}
              {hpRolledValues.map((v, i) => (
                <span key={i}>
                  + <strong className="hp-die-val">[{v}]</strong>
                </span>
              ))}{" "}
              {hpConBonus >= 0 ? `+ ${hpConBonus}` : `− ${Math.abs(hpConBonus)}`} (CON) ={" "}
              <strong className="hp-roll-total">{hpRolled} HP</strong>
            </span>
          </div>

          <div className="hp-roll-actions">
            <button type="button" className="reroll-btn" onClick={handleRollDice} disabled={isRolling}>
              ↺ Rolar Novamente
            </button>
            <button type="button" className="confirm-btn" onClick={onConfirmHp}>
              ✓ Confirmar {hpRolled} HP
            </button>
          </div>
        </>
      )}

      {!isRolling && !hpRolledValues && (
        <button type="button" className="roll-btn" onClick={handleRollDice}>
          🎲 Rolar {diceNeeded}× d{hitDie}
        </button>
      )}
    </div>
  );
}

// ── BACKGROUND BONUS BANNER ──────────────────────────────────

function BackgroundBonusBanner({ state }) {
  const bonuses = ABILITY_KEYS
    .map((key) => ({ key, bonus: state.backgroundStats?.[key] || 0 }))
    .filter((b) => b.bonus > 0);

  if (bonuses.length === 0) return null;

  const bgName = state.background || "Background";

  return (
    <div className="bg-bonus-banner">
      <div className="bg-bonus-banner__title">
        🎒 Bônus de {bgName}
      </div>
      <div className="bg-bonus-banner__tags">
        {bonuses.map(({ key, bonus }) => (
          <span
            key={key}
            className={`bg-bonus-banner__tag ${bonus === 2 ? "bg-bonus-banner__tag--plus2" : "bg-bonus-banner__tag--plus1"}`}
          >
            {ABILITY_NAMES[key]} +{bonus}
          </span>
        ))}
      </div>
      <div className="bg-bonus-banner__hint">
        Esses bônus serão somados automaticamente ao valor base de cada atributo.
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ───────────────────────────────────────────

export default function AttributesStep({
  state,
  onRollSets,
  onAssignRollSlot,
  onAssignStandard,
  onPointBuyChange,
  onConfirmAttributes,
  onSelectHpMethod,
  onRollHp,
  onConfirmHp,
  onConfirmHpFixed,
}) {
  const [selectedPanel, setSelectedPanel] = useState("roll");
  const activePanel = state.attrMode || selectedPanel;

  function handleSelectPanel(panel) {
    if (state.attrMode !== null) return;
    setSelectedPanel(panel);
  }

  const hitDie = state.charClass?.hit_die || 8;
  const conTotal = getAttributeTotal(state, "con");
  const conMod = mod(conTotal);
  const hpFinal = calculateHP(state);

  return (
    <>
      {/* ── SECTION 1: ATTRIBUTES ── */}
      <div className="attr-section">
        <div className="attr-section-header">
          <h3 className="attr-section-title">1 — Atributos</h3>
          {state.attrConfirmed && <span className="section-lock-badge">🔒 Confirmados</span>}
        </div>

        {state.attrConfirmed ? (
          <ConfirmedAttrSummary state={state} />
        ) : (
          <>
            <BackgroundBonusBanner state={state} />
            <MethodSelector
              attrMode={state.attrMode}
              activePanel={activePanel}
              onSelectPanel={handleSelectPanel}
            />

            {activePanel === "roll" && (
              <RollPanel
                state={state}
                onRollSets={onRollSets}
                onAssignRollSlot={onAssignRollSlot}
                onConfirmAttributes={onConfirmAttributes}
              />
            )}
            {activePanel === "standard" && (
              <StandardPanel
                state={state}
                onAssignStandard={onAssignStandard}
                onConfirmAttributes={onConfirmAttributes}
              />
            )}
            {activePanel === "pointbuy" && (
              <PointBuyPanel
                state={state}
                onPointBuyChange={onPointBuyChange}
                onConfirmAttributes={onConfirmAttributes}
              />
            )}
          </>
        )}
      </div>

      {/* ── SECTION 2: HP ── */}
      <div className={`attr-section${!state.attrConfirmed ? " attr-section--locked" : ""}`}>
        <div className="attr-section-header">
          <h3 className="attr-section-title">2 — Vida</h3>
          {state.hpConfirmed && <span className="section-lock-badge">🔒 Confirmada</span>}
        </div>

        {!state.attrConfirmed ? (
          <div className="section-locked-msg">
            ⚔️ Complete os Atributos primeiro para definir sua Vida.
          </div>
        ) : state.hpConfirmed ? (
          <div className="confirmed-summary">
            <div className="confirmed-badge">🔒 Vida Confirmada</div>
            <div className="hp-confirmed-display">
              <span className="hp-confirmed-value">{hpFinal}</span>
              <span className="hp-confirmed-label">HP Máximo</span>
            </div>
          </div>
        ) : (
          <HPSection
            state={state}
            onSelectHpMethod={onSelectHpMethod}
            onRollHp={onRollHp}
            onConfirmHp={onConfirmHp}
            onConfirmHpFixed={onConfirmHpFixed}
          />
        )}
      </div>

      {/* ── FINAL SUMMARY ── */}
      {state.attrConfirmed && state.hpConfirmed && (
        <div className="attr-section attr-final-summary">
          <h3 className="attr-section-title">✦ Resumo Final</h3>
          <div className="confirmed-attrs-grid">
            {ABILITY_KEYS.map((key) => {
              const total = getAttributeTotal(state, key);
              return (
                <div key={key} className="confirmed-attr-cell">
                  <div className="confirmed-attr-label">{key.toUpperCase()}</div>
                  <div className="confirmed-attr-val">{total}</div>
                  <div className="confirmed-attr-mod">{modStr(total)}</div>
                </div>
              );
            })}
          </div>
          {state.charClass && state.startingPointData && (() => {
            const level = state.startingPointData.level;
            const diceNeeded = level - 1;
            const fixedPerLevel = getHpFixedPerLevel(hitDie);
            const hpConBonus = conMod * level;
            const conModLabel = conMod >= 0 ? `+${conMod}` : `${conMod}`;
            return (
              <div className="attr-hp-summary">
                <div className="attr-hp-detail-row">
                  <span>[Nível 1]</span>
                  <span>{hitDie} (d{hitDie} máx)</span>
                </div>
                {diceNeeded > 0 && (
                  <div className="attr-hp-detail-row">
                    <span>[Níveis 2–{level}]</span>
                    {state.hpMethodLocked === "roll" && Array.isArray(state.hpRolledValues) ? (
                      <span>
                        {state.hpRolledValues.map((v, i) => (
                          <span key={i}>{i > 0 ? " + " : ""}<strong>[{v}]</strong></span>
                        ))}{" "}
                        = {state.hpRolledValues.reduce((a, b) => a + b, 0)}
                      </span>
                    ) : (
                      <span>{diceNeeded}× {fixedPerLevel} = {diceNeeded * fixedPerLevel}</span>
                    )}
                  </div>
                )}
                <div className="attr-hp-detail-row">
                  <span>[Bônus CON]</span>
                  <span>{conModLabel} × {level} = {hpConBonus >= 0 ? `+${hpConBonus}` : hpConBonus}</span>
                </div>
                <div className="attr-hp-total-row">
                  <span className="attr-hp-label">HP Máximo:</span>
                  <strong className="attr-hp-value">{hpFinal}</strong>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </>
  );
}

